import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import { CallOutgoing02Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  InputSize,
  Modal,
  Select
} from "shared/components";
import type { BaseOption } from "shared/components";
import {
  LIVE_CALL_STATUSES,
  useSoftphone,
  type SoftphoneStatus
} from "softphone";
import { describeUnavailable } from "softphone/describeUnavailable";
import { PRE_DIAL_MODAL_Z_INDEX } from "softphone/stacking";
import { getErrorMessage } from "utils/error";
import { TwoLineOptionLabel } from "views/inbox/components/TwoLineOptionLabel";
import { distinctPhoneName } from "views/inbox/utils/phoneLabel";
import { formatPhoneDisplay } from "views/phone-numbers/lib/format";
import type { PhoneNumber } from "views/phone-numbers/lib/types";
import { useCallBackNumbers } from "./useCallBackNumbers";

// Same wording as the softphone's own toast for a second dial.
const CALL_IN_PROGRESS =
  "You already have a call in progress. Hang up before starting another.";
const OTHER_NUMBER_NOTE =
  "The lead will see this number as the caller and will reach this number if they call back.";
// Covers a line that is gone, switched off or can't call, and one this user has
// no access to: the list only holds numbers the user can use, so the pop-up
// can't tell them apart.
const LINE_UNAVAILABLE_NOTE =
  "You can't call from the number this call came in on.";

// Why Call back is disabled. A live call gets its own message here: the Inbox
// pop-up leaves that to the call card, but this pop-up is opened on purpose to
// call someone, so it has to say why it can't.
const unavailableReason = (
  status: SoftphoneStatus,
  errorMessage: string | null,
  isReady: boolean
): string | null => {
  if (LIVE_CALL_STATUSES.includes(status)) return CALL_IN_PROGRESS;
  return isReady ? null : describeUnavailable(status, errorMessage);
};

// The grey line under "Call from": the property line isn't in the user's list
// (or the call had none), or the user moved off it. Nothing while the list
// loads or is empty.
const callFromNote = ({
  isLoading,
  count,
  propertyLineId,
  fromId
}: {
  isLoading: boolean;
  count: number;
  propertyLineId: number | undefined;
  fromId: number | undefined;
}): string | null => {
  if (isLoading || count === 0) return null;
  if (propertyLineId === undefined) return LINE_UNAVAILABLE_NOTE;
  return fromId === propertyLineId ? null : OTHER_NUMBER_NOTE;
};

const fromSubtitle = (number: PhoneNumber, isPropertyLine: boolean) =>
  [
    distinctPhoneName(number.name, number.phone_number),
    isPropertyLine ? "Property line" : undefined
  ]
    .filter(Boolean)
    .join(" · ") || undefined;

interface CallBackDetails {
  // The caller to dial back.
  toNumber: string;
  toName?: string | null;
  // The number the missed call came in on; null when the call has none.
  lineNumber?: string | null;
  leadId?: number;
  contactId?: number;
  customerId?: number;
}

const CallBackForm = ({
  onClose,
  toNumber,
  toName,
  lineNumber,
  leadId,
  contactId,
  customerId
}: CallBackDetails & { onClose: () => void }) => {
  const { placeCall, isReady, status, errorMessage } = useSoftphone();
  const { numbers, propertyLineId, isLoading } = useCallBackNumbers(lineNumber);
  // Only the user's own pick; the form remounts on every open, so the pop-up
  // starts on the property line each time rather than remembering.
  const [pickedId, setPickedId] = useState<number>();

  const defaultId = propertyLineId ?? numbers[0]?.id;
  const fromId = numbers.some((number) => number.id === pickedId)
    ? pickedId
    : defaultId;
  const from = numbers.find((number) => number.id === fromId);

  const fromOptions: BaseOption[] = useMemo(
    () =>
      numbers.map((number) => ({
        value: String(number.id),
        label: (
          <TwoLineOptionLabel
            title={formatPhoneDisplay(number.phone_number)}
            subtitle={fromSubtitle(number, number.id === propertyLineId)}
          />
        )
      })),
    [numbers, propertyLineId]
  );

  const toOptions: BaseOption[] = [
    {
      value: toNumber,
      label: (
        <TwoLineOptionLabel
          title={formatPhoneDisplay(toNumber)}
          subtitle={distinctPhoneName(toName, toNumber)}
        />
      )
    }
  ];

  const note = callFromNote({
    isLoading,
    count: numbers.length,
    propertyLineId,
    fromId
  });
  const reason = unavailableReason(status, errorMessage, isReady);

  const handleCall = () => {
    if (!from) return;
    placeCall({
      toNumber,
      fromNumberId: from.id,
      leadId,
      contactId,
      customerId,
      fromLabel: from.name ?? undefined,
      toLabel: toName || undefined
    }).catch((error) => toast.error(getErrorMessage(error)));
    onClose();
  };

  return (
    <>
      <div className="flex flex-col items-center gap-4 px-6 pt-6">
        <div className="flex size-16 items-center justify-center rounded-full bg-alpha-40">
          <HugeiconsIcon
            icon={CallOutgoing02Icon}
            size={24}
            className="text-content-1000"
          />
        </div>
        <h2 className="text-lg font-bold text-content-1000">Call back</h2>
      </div>

      <div className="space-y-4 px-6 py-6">
        <div>
          <Select
            label="Call from"
            searchable={true}
            id="call-back-from"
            hideOptional
            size={InputSize.BASE}
            value={fromId != null ? String(fromId) : ""}
            onChange={(event) => setPickedId(Number(event.target.value))}
            options={fromOptions}
            isDisabled={isLoading || numbers.length === 0}
            placeholder={
              !isLoading && numbers.length === 0
                ? "No voice-capable numbers"
                : "Select…"
            }
            legacyErrorMargins={false}
          />
          {note ? (
            <p className="mt-2 text-xs font-medium text-content-400">{note}</p>
          ) : null}
        </div>
        <Select
          label="To"
          id="call-back-to"
          hideOptional
          size={InputSize.BASE}
          value={toNumber}
          onChange={() => undefined}
          options={toOptions}
          isDisabled
          legacyErrorMargins={false}
        />
      </div>

      <div className="flex flex-col gap-2 px-6 pb-6">
        {reason ? <p className="text-sm text-content-500">{reason}</p> : null}
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          onClick={handleCall}
          disabled={!from || !isReady}
        >
          Call back
        </Button>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </>
  );
};

/**
 * "Call back" on a missed call (CRM-1659): pick which number to call from,
 * starting on the line the lead dialed, then place the call. Mirrors the
 * Inbox's Make a call pop-up; the caller is fixed.
 */
export const CallBackModal = ({
  opened,
  onClose,
  // Dialogs opened from a drawer pass its modal layer; elsewhere the pre-dial
  // layer clears any drawer.
  zIndex = PRE_DIAL_MODAL_Z_INDEX,
  ...details
}: CallBackDetails & {
  opened: boolean;
  onClose: () => void;
  zIndex?: number;
}) => (
  <Modal
    opened={opened}
    onClose={onClose}
    zIndex={zIndex}
    panelClassName="w-full max-w-[420px] flex flex-col"
  >
    <CallBackForm {...details} onClose={onClose} />
  </Modal>
);
